// src/pages/contacts/index.tsx
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAtom, useSetAtom } from 'jotai';
import { Button } from "@/components/ui/button";
import { ContactsTable } from "@/components/contacts/ContactsTable";
import { CreateContactDialog } from "@/components/contacts/CreateContactDialog";
import { ContactFilters } from "@/components/contacts/ContactFilters";
import { SaveFilterDialog } from "@/components/contacts/SaveFilterDialog";
import { ActionBar } from "@/components/contacts/ActionBar";
import { Badge } from "@/components/ui/badge";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useToast } from "@/hooks/use-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Filter, Plus, Save, GitMerge, X, Users } from "lucide-react";
import { contactsFiltersAtom, lastListRouteAtom } from '@/lib/atoms';
import { ImportContactsDialog } from "@/components/contacts/import/ImportContactsDialog";
import { supabase } from "@/lib/supabase";
import { FILTER_PRESETS } from "@/components/layout/FilterNavMenu";
import type { Contact } from "@/types/contact";
import type { UseBulkSelectionReturn } from "@/hooks/useBulkSelection";
import type { Table } from "@tanstack/react-table";

interface GroupFilter {
  id: string;
  name: string;
  contactIds: string[];
}

export function ContactsPage() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filterSectionOpen, setFilterSectionOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [selectionData, setSelectionData] = useState<{
    bulkSelection: UseBulkSelectionReturn;
    selectedContacts: Contact[];
    table: Table<Contact>;
  } | null>(null);
  const [appliedFilters, setAppliedFilters] = useAtom(contactsFiltersAtom);
  const [stagedFilters, setStagedFilters] = useState<any>({});
  const [initialized, setInitialized] = useState(false);
  const [groupFilter, setGroupFilter] = useState<GroupFilter | null>(null);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const setLastListRoute = useSetAtom(lastListRouteAtom);
  useEffect(() => {
    setLastListRoute('/contacts');
  }, [setLastListRoute]);

  // State lifted from ContactsTable - single source of truth for contacts data
  const [totalCount, setTotalCount] = useState<number>(0);
  // invalidateCache held in a ref, not state. Storing it in state caused a
  // render loop: useContacts returns a new invalidateCache function reference
  // whenever its callback deps shift; ContactsTable's data-load effect re-fires
  // with the new ref; setState writes a new function into ContactsPage state;
  // ContactsPage re-renders; ContactsTable re-renders; useContacts returns
  // another new ref; loop. Refs don't trigger re-renders, so the cycle breaks.
  const invalidateCacheRef = useRef<(() => void) | null>(null);

  // Callback for ContactsTable to report its data state
  const handleDataLoad = useCallback((data: { totalCount: number; invalidateCache: () => void }) => {
    setTotalCount(data.totalCount);
    invalidateCacheRef.current = data.invalidateCache;
  }, []);

  // Memoize the filters object passed to ContactsTable. An inline literal here
  // produces a new reference on every parent render, invalidating ContactsTable's
  // useMemo / useEffect deps and amplifying the data-load loop above. Keep
  // reference-stable as long as the underlying values don't change.
  const tableFilters = useMemo(() => ({
    ...appliedFilters,
    ...(groupFilter ? { contactIds: groupFilter.contactIds } : {}),
  }), [appliedFilters, groupFilter]);

  // Load group filter from URL param
  const groupIdParam = searchParams.get('group');
  useEffect(() => {
    if (!groupIdParam || !currentWorkspace) {
      setGroupFilter(null);
      return;
    }

    const loadGroup = async () => {
      try {
        const { data: groupData } = await supabase
          .from('view_group_counts')
          .select('group_name')
          .eq('group_id', groupIdParam)
          .eq('workspace_id', currentWorkspace.id)
          .single();

        const { data: members } = await supabase
          .rpc('get_contacts_by_group_id', { p_group_id: groupIdParam });

        if (groupData && members) {
          setGroupFilter({
            id: groupIdParam,
            name: groupData.group_name,
            contactIds: members.map((m: any) => m.id),
          });
        }
      } catch (err) {
        console.error('[ContactsPage] Failed to load group filter:', err);
      }
    };

    loadGroup();
  }, [groupIdParam, currentWorkspace?.id]);

  const clearGroupFilter = () => {
    setSearchParams({});
    setGroupFilter(null);
  };

  // Handle ?groupName= param - apply as a filter condition
  const groupNameParam = searchParams.get('groupName');
  useEffect(() => {
    if (groupNameParam) {
      // Apply group name as a filter condition
      // Must include 'table' and 'field' for ContactFilters to display the label correctly
      const groupFilter = {
        group_type: 'AND',
        groups: [{
          id: crypto.randomUUID(),
          type: 'AND',
          conditions: [{
            id: crypto.randomUUID(),
            table: 'groups',
            field: 'group_name',
            operator: 'eq',
            value: groupNameParam
          }]
        }]
      };
      setAppliedFilters(groupFilter);
      setStagedFilters(groupFilter);
      setInitialized(true);
      // Clear the URL param after applying
      searchParams.delete('groupName');
      setSearchParams(searchParams, { replace: true });
    }
  }, [groupNameParam]);

  // Handle ?search= param - apply search term as filter
  const searchTermParam = searchParams.get('search');
  useEffect(() => {
    if (searchTermParam) {
      // Apply search term as a filter
      const searchFilter = {
        group_type: 'AND',
        groups: [],
        search: searchTermParam
      };
      setAppliedFilters(searchFilter);
      setStagedFilters(searchFilter);
      setInitialized(true);
      // Clear the URL param after applying
      searchParams.delete('search');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchTermParam]);

  // Handle ?preset= param - apply pre-built filter preset
  const presetParam = searchParams.get('preset');
  useEffect(() => {
    if (presetParam) {
      const preset = FILTER_PRESETS.find((p) => p.id === presetParam);
      if (preset) {
        // Deep clone and regenerate UUIDs for each filter application
        // to ensure unique IDs for React keys
        const clonedFilters = {
          ...preset.filters,
          groups: preset.filters.groups?.map((group) => ({
            ...group,
            id: crypto.randomUUID(),
            conditions: group.conditions.map((condition) => ({
              ...condition,
              id: crypto.randomUUID(),
              // Ensure table is set for proper filter display
              table: condition.table || 'contacts',
            })),
          })),
          sort: preset.filters.sort,
        };
        setAppliedFilters(clonedFilters);
        setStagedFilters(clonedFilters);
        setInitialized(true);
      }
      // Clear the URL param after applying
      searchParams.delete('preset');
      setSearchParams(searchParams, { replace: true });
    }
  }, [presetParam]);

  // Auto-open create dialog when ?new=1 is in URL
  // Auto-open import dialog when ?import=1 is in URL
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setCreateDialogOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get('import') === '1') {
      setImportDialogOpen(true);
      searchParams.delete('import');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Initialize filters on component mount — only reset if atom is still at its
  // empty default. When the atom already has filters (e.g. user navigated away
  // and came back), preserve them so the persisted-filter experience works.
  //
  // Skip entirely when a `?preset=` is present: on a cold load, this effect and
  // the preset effect above both run off the first-render closure (initialized
  // still false, appliedFilters still empty). Without the `!presetParam` guard
  // this effect's else-branch runs and setAppliedFilters({groups: []}) clobbers
  // the preset the other effect just applied — the contact list then loads
  // unfiltered. The preset effect owns initialization when a preset is applied.
  useEffect(() => {
    if (!initialized && !presetParam) {
      const hasPersistedFilters =
        (appliedFilters.groups && appliedFilters.groups.length > 0) ||
        appliedFilters.search;
      if (hasPersistedFilters) {
        // Restore persisted atom filters into local staged state
        setStagedFilters(appliedFilters);
      } else {
        setStagedFilters({group_type: 'AND', groups: []});
        setAppliedFilters({group_type: 'AND', groups: []});
      }
      setInitialized(true);
    }
  }, [initialized, presetParam]);

  const handleApplyFilters = (newFilters: any) => {
    setAppliedFilters(newFilters);
    setStagedFilters(newFilters);
  };

  // Clear all applied filters (filter blocks + search term). The cleared object
  // omits `search`, so this also drops any active search. Writing the empty
  // shape to both the atom and staged state re-syncs ContactFilters' internal UI
  // via its currentFilters useEffect, even when the filter panel is collapsed.
  const handleRemoveFilters = () => {
    const cleared = { group_type: 'AND', groups: [] };
    setAppliedFilters(cleared);
    setStagedFilters(cleared);
  };

  const handleFilterClick = () => {
    const opening = !filterSectionOpen;
    setFilterSectionOpen(opening);

    // Auto-create a default filter block when opening with empty groups
    if (opening && (!stagedFilters.groups || stagedFilters.groups.length === 0)) {
      setStagedFilters({
        group_type: 'AND',
        groups: [{
          id: crypto.randomUUID(),
          type: 'AND',
          conditions: [{
            id: crypto.randomUUID(),
            table: 'contacts',
            field: 'first_name',
            operator: 'eq',
            value: '',
          }],
        }],
      });
    }
  };

  const handleCreateClick = () => {
    setCreateDialogOpen(true);
  };

  const handleContactCreated = () => {
    toast({
      description: "Contact created successfully",
    });
    // Invalidate cache to refetch contacts
    invalidateCacheRef.current?.();
  };

  const handleDuplicatesClick = () => {
    navigate('/duplicates');
  };

  const hasActiveFilters = (appliedFilters.groups && appliedFilters.groups.length > 0) || appliedFilters.search;

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex justify-between items-center p-4">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Contacts</h1>
          <span className="text-muted-foreground self-end mb-1">{totalCount ? `(${totalCount})` : ''}</span>
        </div>
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSaveDialogOpen(true)}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Filter
            </Button>
          )}

          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemoveFilters}
            >
              <X className="h-4 w-4 mr-2" />
              Remove Filters
            </Button>
          )}

          <div className="relative" data-tour-id="contacts.filters-button">
            <Button
              variant="outline"
              size="icon"
              className={`h-9 w-9 ${filterSectionOpen ? 'bg-accent' : ''}`}
              onClick={handleFilterClick}
            >
              <Filter className="h-4 w-4" />
            </Button>
            {hasActiveFilters && (
              <Badge
                variant="secondary"
                className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center rounded-full"
              >
                {(appliedFilters.groups?.length || 0) + (appliedFilters.search ? 1 : 0)}
              </Badge>
            )}
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={handleDuplicatesClick}
            title="Manage Duplicates"
          >
            <GitMerge className="h-4 w-4" />
          </Button>

          <ImportContactsDialog open={importDialogOpen} onOpenChange={setImportDialogOpen} />

          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={handleCreateClick}
            data-tour-id="contacts.new-contact-button"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {groupFilter && (
        <div className="px-4 pb-2">
          <div className="inline-flex items-center gap-2 bg-muted rounded-md px-3 py-1.5">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">Group: {groupFilter.name}</span>
            <span className="text-xs text-muted-foreground">({groupFilter.contactIds.length})</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 hover:bg-background"
              onClick={clearGroupFilter}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {(hasActiveFilters || (selectionData?.bulkSelection?.effectiveCount ?? 0) > 0) && (
        <div className="px-4">
          <ActionBar
            totalCount={totalCount}
            appliedFilters={appliedFilters}
            bulkSelection={selectionData?.bulkSelection ?? null}
            selectedContacts={selectionData?.selectedContacts ?? []}
            table={selectionData?.table ?? null}
            hasActiveFilters={!!hasActiveFilters}
            onInvalidateCache={() => invalidateCacheRef.current?.()}
          />
        </div>
      )}

      {filterSectionOpen && (
        <div className="px-4">
          <div className="border rounded-lg p-4 bg-card">
            <ContactFilters
              onFilterChange={handleApplyFilters}
              currentFilters={stagedFilters}
            />
          </div>
        </div>
      )}

      <div className="flex-1 w-full px-4">
        <ContactsTable
          filters={tableFilters}
          onDataLoad={handleDataLoad}
          onSelectionChange={setSelectionData}
          onCreateContact={() => setCreateDialogOpen(true)}
          onImportContacts={() => setImportDialogOpen(true)}
        />
      </div>

      {currentWorkspace && (
        <CreateContactDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          workspaceId={currentWorkspace.id}
          onSuccess={handleContactCreated}
        />
      )}

      <SaveFilterDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        currentFilters={appliedFilters}
      />
    </div>
  );
}
